import XUICapsule from '@xero/xui/react/capsule';
import Builders from '../../../assets/svgs/Builders';
import BuildersFrameSVG from '../../../assets/svgs/BuildersFrameSVG';
import DetectivesPolaroids from '../../../assets/svgs/DetectivesPolaroids';
import DetectivesSVG from '../../../assets/svgs/DetectivesSVG';

export interface IEducationFlowProps {
    hasPaidPlan: boolean;
}

const EDUCATION_FLOW = (props: IEducationFlowProps) => {
    const WIZARD = [
        {
            SVG: <DetectivesSVG />,
            HEADING: 'Access your Xero contacts in HubSpot',
            CONTENT:
                'Xero and HubSpot continuously sync, so your customer and supplier contact information is available and up to date',
        },
        {
            SVG: <BuildersFrameSVG />,
            HEADING: 'Get a clearer picture of your cash flow',
            CONTENT:
                'Easily see when customers have outstanding invoices or overdue payments and create lists to contact them',
        },
        {
            SVG: <DetectivesPolaroids />,
            HEADING: 'Target the right customers',
            CONTENT: 'Sell your product and services more effectively using your data to create custom lists',
        },
    ];

    if (props.hasPaidPlan) {
        WIZARD.push({
            SVG: <Builders />,
            HEADING: 'Generate invoices from within HubSpot',
            CONTENT:
                'If you have upgraded to the invoicing feature, you and your team can generate draft invoices from within HubSpot. HubSpot will create the invoice automatically in Xero, ready for it to be approved and sent',
        });
    }

    return WIZARD;
};

export default {
    DISPLAY_NAME: 'HubSpot CRM integration',
    INTEGRATION_NAME: 'HubSpot',
    HAS_PARTNER_STORE: true,
    TAGS: ['Marketing', 'Sales', 'Service Software', 'CRM'],
    SMART_LISTS_RESTRICTION_BLURB_PART_1:
        'Unfortunately, you cannot use the segmentation features of this integration. Please visit ',
    SMART_LISTS_RESTRICTION_ABSOLUTE_URL: 'https://central.xero.com/s/article/HubSpot',
    SMART_LISTS_RESTRICTION_DISPLAY_URL: 'www.central.xero.com/s/article/HubSpot',
    SMART_LISTS_RESTRICTION_BLURB_PART_2: ' if you require more information',
    ACCESS_PAGE: {
        FREE_BLURB:
            'HubSpot’s free CRM is everything you need to organise your relationships with your customers. It tracks your calls, emails, meetings, and more automatically, so you can see all your interactions in one place. With all your data consolidated, you have full, up-to-the-minute visibility into your business.',
        PAID_BLURB: (
            <div>
                <p>
                    HubSpot’s free CRM is everything you need to organise your relationships with your customers. It
                    tracks your calls, emails, meetings, and more automatically, so you can see all your interactions in
                    one place. With all your data consolidated, you have full, up-to-the-minute visibility into your
                    business.
                </p>
                <p>
                    The HubSpot CRM integration has a monthly fee of $10/£6 per month, which is in addition to your Xero
                    subscription. This will be charged immediately when you sign up, and then monthly thereafter.
                </p>
            </div>
        ),
        APP_STORE_BILLING_BLURB:
            'The HubSpot CRM integration has two options, a Xero to HubSpot contact sync, and HubSpot to Xero invoicing which gives you and your team the ability to generate invoices directly from within HubSpot.',
    },
    CONNECTED_PAGE: {
        BLURB: 'Below is an activity report showing what we’ve sent from Xero to HubSpot.\n\nYou can come back to this page to see new imports into HubSpot from Xero.',
        SUCCESS_SETUP_MESSAGE: 'We are currently syncing your contacts, this can take a few minutes.',
    },
    SETUP_PAGE: {
        BLURB: 'Select which contact types you would like to sync to Hubspot. Contact types will be represented in Hubspot with custom properties.',
        HEADER_TITLE: 'Sync Xero contacts to Hubspot',
        CONTACT_TYPES_TITLE: 'Contact types',
        CONTACT_TYPES_INFORMATION:
            "A contact who you've previously sent an invoice to, will be added as a 'customer'. Whereas, a contact who has sent you an invoice you've paid, will be added as a 'supplier'.",
        CONTACT_TYPES_PLACEHOLDER: 'Please select a contact type',
        CONTACT_TYPES_FOOTER: 'Only selected contact types with a valid email address will be synced',

        SEGMENTATION_TAGS_TITLE: 'Add segmentation tags',
        SEGMENTATION_TAGS_FOOTER: 'Segmentation tags will be added as custom properties in Hubspot',
    },
    ERROR: {
        BILLING_SUBSCRIPTION_CANCELLED: {
            MESSAGE:
                'It looks like there is a problem with your subscription. To continue using HubSpot, please check your subscription details.',
            ACTION: 'Update subscription',
            ACTION_URL: '',
        },
    },
    EDUCATION_FLOW,
    EDUCATION_FLOW_TITLE: 'HubSpot settings',
    UPDATE: {
        HEADING: '',
        CONTENT_PART_1: '',
        CONTENT_PART_2: '',
        CONTENT_DISPLAY_LINK: '',
        CONTENT_URL: '',
    },
    FREE_PLAN: {
        HEADING: 'Contact sync',
        FEATURES: [
            'Sync contacts from Xero to HubSpot',
            'See which contacts are 7 days overdue in HubSpot',
            'Find contacts that are 30 days outstanding',
            'Target contacts that have or haven’t purchased from you',
        ],
        BUTTON: 'Get contact sync',
        CAPSULE: (
            <XUICapsule className='xui-margin-left-2xsmall xui-heading-small xui-textcolor-muted xui-text-deemphasis' invalidText={"[Placeholder] Invalid"}>
                Free
            </XUICapsule>
        ),
    },
    LEGAL_TEXT: (
        <div>
            By getting the ‘invoicing’ or ‘contact sync’ feature of the HubSpot CRM integration, you acknowledge that
            your use of the integration is subject to Xero’s{' '}
            <a href='https://www.xero.com/nz/about/legal/terms/?escape=true' target='_blank'>
                terms
            </a>{' '}
            and{' '}
            <a href='https://www.xero.com/nz/about/privacy/?escape=true' target='_blank'>
                privacy
            </a>
            . If you get the ‘invoicing’ feature, you also agree to the{' '}
            <a href='https://www.xero.com/nz/signup/offer-details/?escape=true' target='_blank'>
                offer details
            </a>
            . We are a HubSpot affiliate and may receive a commission if you purchase HubSpot.
        </div>
    ),
    PAID_LEGAL_TEXT: (
        <div>
            By setting up this integration, you acknowledge that your use of this integration is subject to Xero's{' '}
            <a href='https://www.xero.com/nz/about/legal/terms/?escape=true' target='_blank'>
                terms
            </a>
            ,{' '}
            <a href='https://www.xero.com/nz/about/privacy/?escape=true' target='_blank'>
                privacy
            </a>{' '}
            and{' '}
            <a href='https://www.xero.com/nz/signup/offer-details/?escape=true' target='_blank'>
                offer details
            </a>
            . We are a HubSpot affiliate and may receive a commission if you purchase HubSpot.
        </div>
    ),
    FREE_LEGAL_TEXT: (
        <div>
            By setting up this integration, you acknowledge that your use of this integration is subject to Xero's{' '}
            <a href='https://www.xero.com/nz/about/legal/terms/?escape=true' target='_blank'>
                terms
            </a>{' '}
            and{' '}
            <a href='https://www.xero.com/nz/about/privacy/?escape=true' target='_blank'>
                privacy
            </a>
            . We are a HubSpot affiliate and may receive a commission if you purchase HubSpot.
        </div>
    ),
    PAID_PLAN: {
        FEATURES: [
            'Sync contacts from Xero to HubSpot',
            'See which contacts are 7 days overdue in HubSpot',
            'Find contacts that are 30 days outstanding',
            'Target contacts that have or haven’t purchased from you',
            'Generate draft invoices within HubSpot',
            'Add contacts from HubSpot in to Xero',
            'Pulls products available in Xero in to HubSpot',
            'Invoice status synced back to HubSpot',
        ],
        BUTTON: 'Get invoicing',
    },
    PAID_PLAN_TEMP: {
        HEADING: 'Invoicing',
        FEATURES: [
            'All the benefits from the contact sync, plus',
            'Generate draft invoices within HubSpot',
            'Add contacts from HubSpot in to Xero',
            'Pulls products available in Xero in to HubSpot',
            'Invoice status synced back to HubSpot',
        ],
        BUTTON: 'Get invoicing',
        PRICE: '$10/£6 per month',
    },
};
